import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ArrowUpRight, Droplets, FileText, Gauge, Plus, Trash2, Upload, UserPlus, X } from "lucide-react";
import { practiceApi, formatDate, openPracticeFile, euro } from "../lib/practiceApi";
import { StatusBadge } from "../components/StatusBadge";
import { SortableTh } from "../components/sorting/SortableTh";
import { ClientFilter, clientOptions } from "../components/filters/ClientFilter";
import { sortRows, useSortPreference } from "../lib/sorting";
import "./wells.css";

const COLUMNS = [
  ["client_name", "Cliente", "text", "sticky-col"], ["name", "Caso / pozzo", "text"], ["municipality", "Comune", "text"],
  ["authorization_number", "N° autorizzazione", "text"], ["issue_date", "Data rilascio", "date"],
  ["renewal_due_date", "Scadenza autorizzazione", "deadline"],
  ["days_to_renewal", "Giorni", "number", "numeric"], ["renewal_status", "Stato rinnovo", "state"],
  ["last_reading_date", "Ultima lettura", "date"], ["reading_outcome", "Esito lettura", "state"],
  ["next_reading_date", "Prossima scadenza", "deadline"], ["reading_status", "Stato lettura", "state"],
  ["agreed", "Compenso", "number", "numeric"], ["collected", "Incassato", "number", "numeric"],
  ["outstanding", "Da incassare", "number", "numeric"], ["payment_status", "Stato saldo", "state"],
];
const COLUMN_GROUPS = [
  ["essenziale", "Essenziale", ["client_name", "name", "municipality", "renewal_due_date", "renewal_status", "reading_status", "payment_status"]],
  ["autorizzazione", "Autorizzazione", ["client_name", "name", "authorization_number", "issue_date", "renewal_due_date", "days_to_renewal", "renewal_status"]],
  ["letture", "Letture contatore", ["client_name", "name", "last_reading_date", "reading_outcome", "next_reading_date", "reading_status"]],
  ["economia", "Economia", ["client_name", "name", "agreed", "collected", "outstanding", "payment_status"]],
  ["tutte", "Tutte le colonne", null],
];
const MONEY_KEYS = ["agreed", "collected", "outstanding"];
const paymentStatus = money => !money || !money.agreed ? "DA DEFINIRE"
  : money.outstanding <= 0 ? "SALDATO" : money.collected > 0 ? "PARZIALE" : "DA INCASSARE";
const DEFAULT_SORT = { key: "renewal_due_date", kind: "deadline", dir: "asc" };

const FILTERS = [["all", "Tutti"], ["regolari", "Regolari"], ["scadenza", "In scadenza"], ["urgenti", "Urgenti"], ["scaduti", "Scaduti"], ["letture", "Lettura da effettuare"]];
const GENERAL_FIELDS = [["location", "Ubicazione / pozzo"], ["municipality", "Comune"], ["address", "Indirizzo"],
                        ["cadastral_sheet", "Foglio"], ["cadastral_parcel", "Particella"], ["coordinates", "Coordinate"], ["well_type", "Tipologia pozzo"]];
const AUTH_FIELDS = [["authorization_number", "Numero autorizzazione"], ["issue_date", "Data rilascio", "date"],
                     ["last_renewal_date", "Data ultimo rinnovo", "date"], ["renewal_due_date", "Scadenza autorizzazione (manuale)", "date"]];
const READING_FIELDS = [["meter_number", "Numero contatore"], ["last_reading_date", "Ultima lettura effettuata", "date"]];
const today = () => new Date().toLocaleDateString("en-CA");

const matches = (well, filter) => filter === "all" ? true
  : filter === "regolari" ? well.renewal_status === "REGOLARE"
  : filter === "scadenza" ? well.renewal_status === "IN SCADENZA"
  : filter === "urgenti" ? well.renewal_status === "URGENTE"
  : filter === "scaduti" ? well.renewal_status === "SCADUTA"
  : well.reading_outcome !== "EFFETTUATA";

export default function WellsPage({ rows, clients, practices, user, onChanged, onOpenPractice }) {
  const [filter, setFilter] = useState("all");
  const [client, setClient] = useState("");
  const [draft, setDraft] = useState(null);
  const [newClient, setNewClient] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [finance, setFinance] = useState({ wells: {}, totals: { agreed: 0, collected: 0, outstanding: 0 } });
  const loadFinance = useCallback(() => practiceApi("/finance/wells").then(setFinance).catch(() => {}), []);
  useEffect(() => { loadFinance(); }, [loadFinance, rows.length]);
  const [group, setGroup] = useState("essenziale");
  const { sort, onSort } = useSortPreference("wells", DEFAULT_SORT);
  const enriched = useMemo(() => rows.map(well => {
    const money = finance.wells[well.id] || { agreed: 0, collected: 0, outstanding: 0 };
    return { ...well, agreed: money.agreed, collected: money.collected, outstanding: money.outstanding, payment_status: paymentStatus(money) };
  }), [rows, finance]);
  const clientNames = useMemo(() => clientOptions(rows.map(r => r.client_name)), [rows]);
  const visible = useMemo(() => sortRows(enriched.filter(w => matches(w, filter) && (!client || (w.client_name || "").trim() === client)), sort?.key ? sort : DEFAULT_SORT), [enriched, filter, client, sort]);
  const columns = useMemo(() => {
    const keys = COLUMN_GROUPS.find(entry => entry[0] === group)?.[2];
    if (!keys) return COLUMNS;
    const byKey = Object.fromEntries(COLUMNS.map(column => [column[0], column]));
    return keys.map(key => byKey[key]).filter(Boolean);
  }, [group]);
  const current = rows.find(w => w.id === openId) || null;
  const money = visible.reduce((acc, well) => {
    const item = finance.wells[well.id] || { agreed: 0, collected: 0, outstanding: 0 };
    return { agreed: acc.agreed + item.agreed, collected: acc.collected + item.collected, outstanding: acc.outstanding + item.outstanding };
  }, { agreed: 0, collected: 0, outstanding: 0 });

  async function create(event) {
    event.preventDefault();
    setBusy(true); setError("");
    try {
      const created = await practiceApi("/wells", { method: "POST", body: JSON.stringify({ data: draft }) });
      setDraft(null); onChanged(); setOpenId(created.id);
    } catch (err) { setError(err.message); } finally { setBusy(false); }
  }
  async function saveClient(event) {
    event.preventDefault();
    setBusy(true); setError("");
    try {
      const created = await practiceApi("/clients", { method: "POST", body: JSON.stringify({ data: newClient }) });
      setDraft(d => ({ ...d, client_id: created.id }));
      setNewClient(null); onChanged();
    } catch (err) { setError(err.message); } finally { setBusy(false); }
  }
  async function remove(well) {
    if (!window.confirm(`Eliminare il caso pozzo ${well.name || ""}?`)) return;
    setError("");
    try { await practiceApi(`/wells/${well.id}`, { method: "DELETE" }); setOpenId(null); onChanged(); }
    catch (err) { setError(err.message); }
  }

  if (current) return <WellDetail well={current} finance={finance.wells[current.id]} onFinanceChanged={loadFinance} clients={clients} practices={practices} user={user}
    onBack={() => setOpenId(null)} onChanged={onChanged} onDelete={() => remove(current)} onOpenPractice={onOpenPractice} />;

  return <section className="wells-page" data-testid="wells-page">
    <div className="practice-action-bar">
      <span className="muted" data-testid="wells-count">{visible.length} casi pozzo{visible.length !== rows.length ? ` su ${rows.length}` : ""}</span>
      <button className="primary" data-testid="well-create-button" onClick={() => setDraft({ name: "", client_id: "", practice_id: "", location: "", municipality: "", address: "", authorization_number: "", issue_date: "", last_renewal_date: "", meter_number: "", last_reading_date: "", notes: "" })}><Plus size={15} />Nuovo caso pozzo</button>
    </div>
    {error && <div className="error" role="alert" data-testid="wells-error">{error}</div>}
    <div className="wells-money" data-testid="wells-money">
      <div data-testid="wells-money-agreed"><span>Totale compensi</span><strong>{euro(money.agreed)}</strong><small>{visible.length} casi pozzo</small></div>
      <div className="ok" data-testid="wells-money-collected"><span>Incassato</span><strong>{euro(money.collected)}</strong><small>Incassi registrati sui pozzi</small></div>
      <div className="due" data-testid="wells-money-outstanding"><span>Da incassare</span><strong>{euro(money.outstanding)}</strong><small>Rientra nei totali dello studio</small></div>
    </div>
    <div className="wells-filters" data-testid="wells-filters">
      {FILTERS.map(([key, label]) => <button key={key} className={filter === key ? "active" : ""} data-testid={`wells-filter-${key}`} onClick={() => setFilter(key)}>{label}</button>)}
      <ClientFilter value={client} onChange={setClient} options={clientNames} testId="wells-filter-client" />
    </div>

    <div className="practice-column-tabs" data-testid="wells-column-tabs" role="tablist">
      {COLUMN_GROUPS.map(([key, label, keys]) => <button key={key} role="tab" aria-selected={group === key}
        className={group === key ? "active" : ""} data-testid={`wells-columns-${key}`} onClick={() => setGroup(key)}>
        {label}<small>{(keys || COLUMNS.map(c => c[0])).length}</small></button>)}
    </div>

    <div className="wells-scroll">
      <table className="wells-table" data-testid="wells-table">
        <thead><tr>
          {columns.map(([key, label, kind, extra]) => <SortableTh key={key} label={label} sortKey={key} kind={kind}
            sort={sort} onSort={onSort} className={extra || ""} testId={`well-sort-${key}`} />)}
          <th />
        </tr></thead>
        <tbody>
          {visible.map(well => <tr key={well.id} data-testid={`well-row-${well.id}`}>
            {columns.map(([key, , kind, extra]) => key === "client_name"
              ? <th className="wells-client" key={key} data-testid={`well-client-${well.id}`}>{well.client_name ? <strong>{well.client_name}</strong> : <span className="muted">Cliente da collegare</span>}</th>
              : key === "name"
              ? <td className="wells-practice" key={key}>
                  <button className="link" data-testid={`well-open-${well.id}`} onClick={() => setOpenId(well.id)}>{well.name || "Caso pozzo"}<ArrowUpRight size={13} /></button>
                  <small>{well.location || "Ubicazione da compilare"}</small>
                </td>
              : <td key={key} className={extra || ""} data-testid={`well-cell-${key}-${well.id}`}>
                  {kind === "state" ? <StatusBadge status={well[key]} testId={`well-${key}-${well.id}`} />
                    : MONEY_KEYS.includes(key) ? euro(well[key])
                    : ["date", "deadline"].includes(kind) ? formatDate(well[key])
                    : well[key] ?? "—"}
                </td>)}
            <td>{(user.role === "admin" || well.assigned_to === user.id) && <button className="icon-btn" title="Elimina caso pozzo" data-testid={`well-delete-${well.id}`} onClick={() => remove(well)}><Trash2 size={14} /></button>}</td>
          </tr>)}
        </tbody>
      </table>
      {!visible.length && <div className="practice-empty" data-testid="wells-empty"><Droplets size={28} /><strong>{rows.length ? "Nessun caso con questo filtro" : "Nessun caso pozzo registrato"}</strong><span>Crea un caso pozzo: il collegamento a una pratica è facoltativo.</span></div>}
    </div>

    {draft && <div className="modal-backdrop"><form className="create-modal wells-modal" onSubmit={create} data-testid="well-create-modal">
      <button type="button" className="modal-close" data-testid="well-create-close" onClick={() => setDraft(null)}><X /></button>
      <p className="eyebrow">POZZI ARTESIANI</p>
      <h2>Nuovo caso pozzo</h2>
      <label>Nome / identificativo del caso<input required data-testid="well-draft-name" value={draft.name} onChange={e => setDraft({ ...draft, name: e.target.value })} /></label>
      <label>Cliente
        <select required data-testid="well-draft-client" value={draft.client_id} onChange={e => setDraft({ ...draft, client_id: e.target.value })}>
          <option value="">Seleziona dall'anagrafica</option>
          {[...clients].sort((a, b) => (a.name || "").localeCompare(b.name || "", "it")).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </label>
      <button type="button" className="ghost" data-testid="well-draft-new-client" onClick={() => setNewClient({ name: "", fiscal_code: "", phone: "", email: "" })}><UserPlus size={14} />Nuovo cliente senza uscire</button>
      <label>Pratica collegata (facoltativa)
        <select data-testid="well-draft-practice" value={draft.practice_id} onChange={e => setDraft({ ...draft, practice_id: e.target.value })}>
          <option value="">Nessuna pratica collegata</option>
          {[...practices].sort((a, b) => (a.name || "").localeCompare(b.name || "", "it")).map(p => <option key={p.id} value={p.id}>{p.name || "Pratica"}</option>)}
        </select>
      </label>
      <label>Ubicazione<input data-testid="well-draft-location" value={draft.location} onChange={e => setDraft({ ...draft, location: e.target.value })} /></label>
      <label>Comune<input data-testid="well-draft-municipality" value={draft.municipality} onChange={e => setDraft({ ...draft, municipality: e.target.value })} /></label>
      <label>Numero autorizzazione<input data-testid="well-draft-authorization" value={draft.authorization_number} onChange={e => setDraft({ ...draft, authorization_number: e.target.value })} /></label>
      <label>Data rilascio<input type="date" data-testid="well-draft-issue" value={draft.issue_date} onChange={e => setDraft({ ...draft, issue_date: e.target.value })} /></label>
      <label>Data ultimo rinnovo<input type="date" data-testid="well-draft-renewal" value={draft.last_renewal_date} onChange={e => setDraft({ ...draft, last_renewal_date: e.target.value })} /></label>
      <label>Numero contatore<input data-testid="well-draft-meter" value={draft.meter_number} onChange={e => setDraft({ ...draft, meter_number: e.target.value })} /></label>
      <label>Ultima lettura effettuata<input type="date" data-testid="well-draft-reading" value={draft.last_reading_date} onChange={e => setDraft({ ...draft, last_reading_date: e.target.value })} /></label>
      <label>Note<textarea rows={2} data-testid="well-draft-notes" value={draft.notes} onChange={e => setDraft({ ...draft, notes: e.target.value })} /></label>
      <button className="primary wide" type="submit" disabled={busy} data-testid="well-draft-save"><Gauge size={15} />{busy ? "Salvataggio…" : "Crea caso pozzo"}</button>
    </form></div>}

    {newClient && <div className="modal-backdrop"><form className="create-modal" onSubmit={saveClient} data-testid="well-client-modal">
      <button type="button" className="modal-close" data-testid="well-client-close" onClick={() => setNewClient(null)}><X /></button>
      <p className="eyebrow">ANAGRAFICA CLIENTI</p>
      <h2>Nuovo cliente</h2>
      <label>Nome / ragione sociale<input required data-testid="well-client-name" value={newClient.name} onChange={e => setNewClient({ ...newClient, name: e.target.value })} /></label>
      <label>Codice fiscale / P. IVA<input data-testid="well-client-fiscal" value={newClient.fiscal_code} onChange={e => setNewClient({ ...newClient, fiscal_code: e.target.value })} /></label>
      <label>Telefono<input data-testid="well-client-phone" value={newClient.phone} onChange={e => setNewClient({ ...newClient, phone: e.target.value })} /></label>
      <label>Email<input type="email" data-testid="well-client-email" value={newClient.email} onChange={e => setNewClient({ ...newClient, email: e.target.value })} /></label>
      <button className="primary wide" type="submit" disabled={busy} data-testid="well-client-save">{busy ? "Salvataggio…" : "Salva cliente"}</button>
    </form></div>}
  </section>;
}

const WellDetail = ({ well, finance, onFinanceChanged, clients, practices, user, onBack, onChanged, onDelete, onOpenPractice }) => {
  const [form, setForm] = useState(well);
  const [payment, setPayment] = useState(null);
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");
  const set = (field, value) => setForm(f => ({ ...f, [field]: value }));

  const loadFiles = useCallback(() => practiceApi(`/files?collection=wells&record_id=${well.id}`).then(setFiles).catch(() => setFiles([])), [well.id]);
  useEffect(() => { loadFiles(); }, [loadFiles]);

  async function save() {
    setBusy(true); setError(""); setNotice("");
    const keys = ["name", "client_id", "practice_id", "location", "municipality", "address", "cadastral_sheet", "cadastral_parcel",
                  "coordinates", "well_type", "authorization_number", "issue_date", "last_renewal_date", "renewal_due_date",
                  "meter_number", "last_reading_date", "notes", "agreed_amount"];
    try {
      await practiceApi(`/wells/${well.id}`, { method: "PUT", body: JSON.stringify({ data: Object.fromEntries(keys.map(k => [k, form[k] ?? ""])) }) });
      setNotice("Caso pozzo aggiornato"); onChanged();
    } catch (err) { setError(err.message); } finally { setBusy(false); }
  }
  async function upload(selected) {
    setBusy(true); setError("");
    try {
      for (const file of selected) {
        const body = new FormData();
        body.append("file", file); body.append("collection", "wells"); body.append("record_id", well.id);
        await practiceApi("/files/upload", { method: "POST", body });
      }
      await loadFiles();
    } catch (err) { setError(err.message); } finally { setBusy(false); }
  }
  async function removeFile(file) {
    try { await practiceApi(`/files/${file.id}`, { method: "DELETE" }); await loadFiles(); }
    catch (err) { setError(err.message); }
  }

  const economics = finance || { agreed: 0, collected: 0, outstanding: 0, payments: [] };
  async function savePayment(event) {
    event.preventDefault();
    setBusy(true); setError("");
    try {
      await practiceApi("/workspace/advances", { method: "POST", body: JSON.stringify({ ...payment, well_id: well.id, amount: Number(payment.amount) }) });
      setPayment(null); onFinanceChanged();
    } catch (err) { setError(err.message); } finally { setBusy(false); }
  }
  async function removePayment(row) {
    if (!window.confirm("Eliminare questo incasso?")) return;
    try { await practiceApi(`/workspace/advances/${row.id}`, { method: "DELETE" }); onFinanceChanged(); }
    catch (err) { setError(err.message); }
  }

  const field = ([key, label, kind]) => <label key={key}>{label}
    <input type={kind === "date" ? "date" : "text"} data-testid={`well-field-${key}`} value={form[key] || ""} onChange={e => set(key, e.target.value)} />
  </label>;

  return <section className="wells-page practice-workspace" data-testid="well-detail">
    <div className="practice-action-bar">
      <button className="ghost" data-testid="well-back" onClick={onBack}>← Tutti i casi pozzo</button>
      <div className="practice-inline-actions">
        {(user.role === "admin" || well.assigned_to === user.id) && <button className="ghost" data-testid="well-detail-delete" onClick={onDelete}><Trash2 size={15} />Elimina caso</button>}
        <button className="primary" data-testid="well-save" disabled={busy} onClick={save}>{busy ? "Salvataggio…" : "Salva modifiche"}</button>
      </div>
    </div>
    {error && <div className="error" role="alert" data-testid="well-detail-error">{error}</div>}
    {notice && <div className="flash" data-testid="well-detail-notice">{notice}</div>}
    <header className="practice-section-heading"><h2><Droplets size={17} />{form.name || "Caso pozzo"}</h2>
      <span>{well.client_name || "Cliente da collegare"} · autorizzazione {well.authorization_number || "da inserire"}</span></header>

    <fieldset className="practice-fieldset"><legend>Dati generali</legend><div className="practice-form-grid">
      <label>Nome / identificativo<input data-testid="well-field-name" value={form.name || ""} onChange={e => set("name", e.target.value)} /></label>
      <label>Cliente
        <select data-testid="well-field-client" value={form.client_id || ""} onChange={e => set("client_id", e.target.value)}>
          <option value="">Seleziona cliente</option>
          {[...clients].sort((a, b) => (a.name || "").localeCompare(b.name || "", "it")).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </label>
      {GENERAL_FIELDS.map(field)}
    </div></fieldset>

    <fieldset className="practice-fieldset"><legend>Autorizzazione</legend><div className="practice-form-grid">
      {AUTH_FIELDS.map(field)}
      <div><span className="practice-field-label">Scadenza calcolata</span><div className="practice-field-value" data-testid="well-detail-due">{formatDate(well.renewal_due_date)}</div></div>
      <div><span className="practice-field-label">Giorni al rinnovo</span><div className="practice-field-value" data-testid="well-detail-days">{well.days_to_renewal ?? "—"}</div></div>
      <div><span className="practice-field-label">Stato rinnovo</span><div className="practice-field-value"><StatusBadge status={well.renewal_status} testId="well-detail-renewal-status" /></div></div>
    </div></fieldset>

    <fieldset className="practice-fieldset"><legend>Letture contatore</legend><div className="practice-form-grid">
      {READING_FIELDS.map(field)}
      <div><span className="practice-field-label">Esito lettura</span><div className="practice-field-value"><StatusBadge status={well.reading_outcome} testId="well-detail-reading-outcome" /></div></div>
      <div><span className="practice-field-label">Prossima scadenza</span><div className="practice-field-value" data-testid="well-detail-next-reading">{formatDate(well.next_reading_date)}</div></div>
      <div><span className="practice-field-label">Giorni alla lettura</span><div className="practice-field-value">{well.days_to_reading ?? "—"}</div></div>
      <div><span className="practice-field-label">Stato lettura</span><div className="practice-field-value"><StatusBadge status={well.reading_status} testId="well-detail-reading-status" /></div></div>
    </div></fieldset>

    <fieldset className="practice-fieldset" data-testid="well-economics"><legend>Economia del caso pozzo</legend>
      <div className="practice-form-grid">
        <label>Compenso concordato (€)
          <input type="number" min="0" step="0.01" data-testid="well-field-agreed" value={form.agreed_amount ?? ""} onChange={e => set("agreed_amount", e.target.value)} />
        </label>
        <div><span className="practice-field-label">Incassato</span><div className="practice-field-value" data-testid="well-collected">{euro(economics.collected)}</div></div>
        <div><span className="practice-field-label">Da incassare</span><div className="practice-field-value" data-testid="well-outstanding">{euro(economics.outstanding)}</div></div>
      </div>
      <div className="practice-action-bar">
        <span className="muted">{economics.payments.length} incassi registrati · confluiscono nei totali dello studio</span>
        <button className="primary" data-testid="well-payment-add" onClick={() => setPayment({ amount: "", date: today(), description: "", payment_method: "Bonifico" })}>+ Registra incasso</button>
      </div>
      {payment && <form className="practice-form-grid advance-form" data-testid="well-payment-form" onSubmit={savePayment}>
        <label>Importo ricevuto (€)<input required type="number" min="0.01" step="0.01" data-testid="well-payment-amount" value={payment.amount} onChange={e => setPayment({ ...payment, amount: e.target.value })} /></label>
        <label>Data incasso<input required type="date" data-testid="well-payment-date" value={payment.date} onChange={e => setPayment({ ...payment, date: e.target.value })} /></label>
        <label>Causale<input data-testid="well-payment-description" value={payment.description} onChange={e => setPayment({ ...payment, description: e.target.value })} /></label>
        <label>Modalità<select data-testid="well-payment-method" value={payment.payment_method} onChange={e => setPayment({ ...payment, payment_method: e.target.value })}>{["Contanti", "Bonifico", "Assegno"].map(m => <option key={m} value={m}>{m}</option>)}</select></label>
        <div className="practice-inline-actions">
          <button type="button" className="ghost" data-testid="well-payment-cancel" onClick={() => setPayment(null)}>Annulla</button>
          <button className="primary" type="submit" disabled={busy} data-testid="well-payment-save">{busy ? "Salvataggio…" : "Salva incasso"}</button>
        </div>
      </form>}
      {economics.payments.map(row => <div className="advance-row" key={row.id} data-testid={`well-payment-${row.id}`}>
        <time>{formatDate(row.date)}</time>
        <div><strong>{row.description || "Incasso"}</strong><small className="advance-method">{row.payment_method || "Bonifico"}</small></div>
        <strong>{euro(row.amount)}</strong>
        <button className="icon-btn" title="Elimina incasso" data-testid={`well-payment-delete-${row.id}`} onClick={() => removePayment(row)}><Trash2 size={14} /></button>
      </div>)}
      {!economics.payments.length && !payment && <p className="practice-hint" data-testid="well-payments-empty">Nessun incasso registrato su questo caso pozzo.</p>}
    </fieldset>

    <fieldset className="practice-fieldset"><legend>Documenti</legend>
      <label className="primary well-upload" data-testid="well-upload-label"><Upload size={15} />{busy ? "Caricamento…" : "Carica documenti"}
        <input type="file" hidden multiple disabled={busy} data-testid="well-upload-input" onChange={e => { const selected = Array.from(e.target.files); e.target.value = ""; if (selected.length) upload(selected); }} />
      </label>
      {files.map(file => <div className="practice-file" key={file.id} data-testid={`well-file-${file.id}`}>
        <span className="practice-file-icon"><FileText size={17} /></span>
        <div className="practice-file-info"><strong>{file.original_filename}</strong><small>{formatDate((file.created_at || "").slice(0, 10))} · {file.uploader_name || ""}</small></div>
        <div className="practice-file-actions">
          <button className="icon-btn" title="Apri documento" data-testid={`well-file-open-${file.id}`} onClick={() => openPracticeFile(file)}><ArrowUpRight size={14} /></button>
          <button className="icon-btn" title="Elimina documento" data-testid={`well-file-delete-${file.id}`} onClick={() => removeFile(file)}><Trash2 size={14} /></button>
        </div>
      </div>)}
      {!files.length && <p className="practice-hint" data-testid="well-files-empty">Nessun allegato: carica autorizzazioni, rinnovi, comunicazioni, ricevute o letture.</p>}
    </fieldset>

    <fieldset className="practice-fieldset"><legend>Note</legend>
      <label className="field-wide">Note tecniche e operative<textarea rows={4} data-testid="well-field-notes" value={form.notes || ""} onChange={e => set("notes", e.target.value)} /></label>
    </fieldset>

    <fieldset className="practice-fieldset"><legend>Pratica collegata (facoltativa)</legend><div className="practice-form-grid">
      <label>Pratica
        <select data-testid="well-field-practice" value={form.practice_id || ""} onChange={e => set("practice_id", e.target.value)}>
          <option value="">Nessuna pratica collegata</option>
          {[...practices].sort((a, b) => (a.name || "").localeCompare(b.name || "", "it")).map(p => <option key={p.id} value={p.id}>{p.name || "Pratica"}</option>)}
        </select>
      </label>
      {well.practice_id && <div><span className="practice-field-label">Apri la pratica</span>
        <div className="practice-field-value"><button className="link" data-testid="well-open-practice" onClick={() => onOpenPractice(well.practice_id)}>{well.practice_name || "Pratica collegata"} <ArrowUpRight size={13} /></button></div></div>}
    </div></fieldset>
  </section>;
};
